"use client";

import * as React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { cn } from "../libs/utils";
import { Button } from "..//components/ui/Button";
import { Dismiss16Regular } from "@fluentui/react-icons";

type DrawerPosition = "left" | "right" | "top" | "bottom";

type DrawerConfig = {
  Component?: React.ComponentType;
  element?: React.ReactNode;
  closable?: boolean;
  width?: string;
  fullScreen?: boolean;
  naked?: boolean;
  showBackdrop?: boolean;
  position?: DrawerPosition;
  style?: React.CSSProperties;
  className?: string;
};

type DrawerProps = {
  open: (e: DrawerConfig) => void;
  close: () => void;
  Drawer?: any;
};

export function useDrawer(): DrawerProps {
  const [isOpen, setIsOpen] = React.useState(false);
  const [config, setConfig] = React.useState<DrawerConfig | null>(null);

  const open = (conf: DrawerConfig) => {
    setConfig(conf);
    setIsOpen(true);
  };

  const close = () => {
    setIsOpen(false);
    setConfig(null);
  };

  const ANIMATION_DURATION = 300; // ms

  const Drawer = () => {
    const [isVisible, setIsVisible] = React.useState(false);
    const [shouldRender, setShouldRender] = React.useState(isOpen);

    React.useEffect(() => {
      if (isOpen) {
        setShouldRender(true);
        // Wait next frame to trigger animation
        requestAnimationFrame(() => setIsVisible(true));
      } else {
        setIsVisible(false);
        const timeout = setTimeout(
          () => setShouldRender(false),
          ANIMATION_DURATION,
        );
        return () => clearTimeout(timeout);
      }
    }, [isOpen]);

    if (!config || !shouldRender) return null;

    const positionClass = (() => {
      switch (config.position) {
        case "left":
          return "left-0 top-0 h-full";
        case "right":
          return "right-0 top-0 h-full";
        case "top":
          return "top-0 left-0 w-full";
        case "bottom":
          return "bottom-0 left-0 w-full";
        default:
          return "right-0 top-0 h-full";
      }
    })();

    const animationBaseClasses =
      "fixed z-50 bg-background shadow-lg transition-opacity transition-transform duration-300 ease-in-out";

    const translateYClass =
      config.position === "top"
        ? isVisible
          ? "opacity-100 translate-y-0"
          : "opacity-0 -translate-y-10"
        : isVisible
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-10";

    return (
      <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
        <Dialog.Portal>
          {config.showBackdrop !== false && (
            <Dialog.Overlay
              className={`fixed inset-0 bg-black/50 z-50 transition-opacity duration-300 ease-in-out ${
                isVisible ? "opacity-100" : "opacity-0"
              }`}
            />
          )}
          <Dialog.Content
            className={cn(
              animationBaseClasses,
              positionClass,
              config.naked ? "p-0" : "p-6",
              translateYClass,
              "border border-grey-200 dark:border-grey-800 overflow-y-auto",
              config.className,
              config.style,
            )}
            style={{ ...config.style }}
            aria-describedby={undefined}
          >
            <Dialog.Title></Dialog.Title>
            {config.closable && (
              <Button
                onClick={close}
                size="icon"
                variant="ghost"
                className="h-9 w-9 absolute right-2 top-2"
              >
                <Dismiss16Regular />
              </Button>
            )}
            {config.Component && <config.Component />}
            {config.element}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  };

  return { open, close, Drawer };
}
